import type {DisplayDecimal} from './symbol-decimals';

const DIGITS = new Uint8Array(80); // raw digits
const OUT = new Uint16Array(90); // resulting char codes

const NEGATIVE_SIGN_CODE = 45; // "-"
const GROUPS_DELIMITER_CODE = 44; // ","
const ZERO_CHAR_CODE = 48; // "0"
const DOT_CHAR_CODE = 46; // "."

const MAX_DECIMAL_PLACES = 18;

let zeroClasses : string[];
let dotZeroClasses : string[];

const initCssClasses = () => {
    if (zeroClasses) {
        return;
    }

    zeroClasses = new Array(MAX_DECIMAL_PLACES);
    dotZeroClasses = new Array(MAX_DECIMAL_PLACES);

    for (let i = 0; i < MAX_DECIMAL_PLACES; i++) {
        zeroClasses[i] = `-dec-0-${i + 1}`;
        dotZeroClasses[i] = `-dec-dot-0-${i + 1}`;
    }
};

const EMPTY_DISPLAY_DECIMAL: DisplayDecimal<string> = {
    value: '',
    class: '',
    originalValue: undefined
};

export const createDisplayDecimalFromBigint = (val: bigint | string, decimals: number): DisplayDecimal<string> => {
    if (!val && val !== 0n) {
        return EMPTY_DISPLAY_DECIMAL;
    }

    if (typeof val === 'string') {
        val = BigInt(val);
    }

    if (decimals < 0 || decimals > 30) throw RangeError('decimals');

    const neg = val < 0n;
    let v = neg ? -val : val;

    // Put all digits to DIGITS (from right to left)
    let ptr = 0;
    let trailZero = 0;
    let fracSignificant = 0;

    // Fraction part
    if (decimals) {
        let isTrailingZeroEnded = false;
        for (let i = 0; i < decimals; i++) {
            const d = Number(v % 10n);
            v = v / 10n;

            if (!isTrailingZeroEnded) {
                if (d === 0) {
                    trailZero++;
                    continue;
                } else {
                    isTrailingZeroEnded = true;
                }
            }

            DIGITS[ptr++] = d;
        }
        fracSignificant = ptr;
    }

    const fracLen = ptr;

    // Integer part
    if (v === 0n) DIGITS[ptr++] = 0;
    else while (v > 0n) {
        DIGITS[ptr++] = Number(v % 10n);
        v = v / 10n;
    }

    const intLen = ptr - fracLen;

    // Format OUT (from right to left)
    let o = 0;
    if (neg) OUT[o++] = NEGATIVE_SIGN_CODE;

    // Integer part + groups
    const firstIntDigitIndex = ptr - 1;
    if (intLen === 1) {
        OUT[o++] = ZERO_CHAR_CODE + DIGITS[firstIntDigitIndex];
    } else {
        const lastIntDigitIndex = ptr - intLen;
        let initDigitsLeft = intLen;
        for (let i = firstIntDigitIndex; i >= lastIntDigitIndex; i--) {
            OUT[o++] = ZERO_CHAR_CODE + DIGITS[i];
            initDigitsLeft--;

            if (initDigitsLeft && initDigitsLeft % 3 === 0) {
                OUT[o++] = GROUPS_DELIMITER_CODE;
            }
        }
    }

    // Fraction part
    if (fracSignificant) {
        OUT[o++] = DOT_CHAR_CODE;
        for (let i = fracSignificant - 1; i >= 0; i--)
            OUT[o++] = ZERO_CHAR_CODE + DIGITS[i];
    }

    const value = String.fromCharCode(...OUT.subarray(0, o));

    // CSS class
    initCssClasses();
    let cls = '';
    if (fracSignificant === 0) {
        if (decimals) {
            cls = dotZeroClasses[decimals - 1] || `-dec-dot-0-${decimals}`;
        }
    } else if (trailZero > 0) {
        cls = zeroClasses[trailZero - 1] || `-dec-0-${trailZero}`;
    }

    return {
        value,
        class: cls,
        originalValue: undefined // the field doesn't support bigint
    };
};
